import { useState } from "react";
import { StatusBadge } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Copy, Download, ChevronDown, Cpu } from "lucide-react";
import { toast } from "sonner";

export function LicenseCard({ lic }) {
  const [open, setOpen] = useState(false);

  const copyToken = () => {
    navigator.clipboard.writeText(lic.token);
    toast.success("License token copied");
  };

  const download = () => {
    const blob = new Blob([lic.token], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${lic.plugin_slug}-${lic.locking_id}.lic`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("License file downloaded");
  };

  const expiry = lic.expires_at
    ? new Date(lic.expires_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : "Perpetual";

  return (
    <div
      data-testid={`license-card-${lic.id}`}
      className="border border-zinc-800 rounded-md bg-zinc-950 hover:border-zinc-700 transition-colors"
    >
      <div className="p-5 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Cpu className="h-4 w-4 text-orange-500" />
            <h3 className="font-semibold text-lg truncate">{lic.plugin_name}</h3>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1 font-mono text-xs text-zinc-400">
            <span>LOCK ID: <span className="text-zinc-200">{lic.locking_id}</span></span>
            {lic.mac_address && <span>MAC: <span className="text-zinc-200">{lic.mac_address}</span></span>}
            <span>TYPE: <span className="text-zinc-200">{lic.type}</span></span>
            <span>EXPIRES: <span className="text-zinc-200">{expiry}</span></span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={lic.status} />
          <Button
            data-testid={`license-toggle-${lic.id}`}
            variant="ghost"
            size="sm"
            className="rounded-sm text-zinc-400 hover:text-white hover:bg-zinc-800"
            onClick={() => setOpen(!open)}
          >
            Token <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${open ? "rotate-180" : ""}`} />
          </Button>
        </div>
      </div>

      {open && (
        <div className="border-t border-zinc-800 p-5">
          <pre
            data-testid={`license-token-${lic.id}`}
            className="bg-black border border-zinc-800 rounded-md p-4 font-mono text-xs text-orange-400 break-all whitespace-pre-wrap max-h-40 overflow-auto"
          >
            {lic.token}
          </pre>
          <div className="flex gap-3 mt-3">
            <Button
              data-testid={`license-copy-${lic.id}`}
              size="sm"
              className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white hover:text-white"
              onClick={copyToken}
            >
              <Copy className="h-4 w-4 mr-1.5" /> Copy
            </Button>
            <Button
              data-testid={`license-download-${lic.id}`}
              size="sm"
              variant="outline"
              className="rounded-sm border-zinc-700 hover:bg-zinc-800 hover:text-white"
              onClick={download}
            >
              <Download className="h-4 w-4 mr-1.5" /> Download .lic
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
